import React from 'react';
import { motion } from 'framer-motion';
import { Award, GraduationCap, Trophy } from 'lucide-react';

const OurAttorneys = () => {
  const openLeadForm = () => {
    window.dispatchEvent(new CustomEvent('openLeadForm'));
  };

  const attorneys = [
    {
      name: 'Robert Harrison',
      title: 'Managing Partner',
      specialization: 'Corporate Law & Mergers & Acquisitions',
      description: 'Robert leads our corporate practice with extensive experience in complex business transactions, mergers, and acquisitions. He has successfully represented major corporations and emerging businesses in significant transactions. His strategic approach combines deep legal expertise with practical business acumen, helping clients navigate sophisticated corporate restructurings, joint ventures, and cross-border transactions. Robert is recognized for his ability to structure deals that protect client interests while achieving optimal business outcomes.',
      education: [
        'J.D., Harvard Law School, magna cum laude',
        'B.A., Economics, Yale University'
      ],
      achievements: [
        'Named "Corporate Attorney of the Year" by Legal Excellence Magazine',
        'Successfully closed numerous complex M&A transactions',
        'Published author on corporate governance and securities law',
        'Board member of American Corporate Counsel Association'
      ]
    },
    {
      name: 'Victoria Chen',
      title: 'Senior Partner',
      specialization: 'Intellectual Property & Technology Law',
      description: 'Victoria is a nationally recognized expert in intellectual property law with extensive experience protecting brands and innovations. She specializes in trademark and copyright registration, portfolio management, and IP litigation. Victoria has successfully registered numerous trademarks and defended clients in complex infringement cases. Her practice extends to technology transactions, licensing agreements, and IP strategy development. She regularly advises technology companies, entertainment industry clients, and consumer brands on protecting their most valuable assets.',
      education: [
        'J.D., Stanford Law School, Order of the Coif',
        'M.S., Computer Science, MIT',
        'B.S., Electrical Engineering, University of California'
      ],
      achievements: [
        'Registered numerous trademarks for global brands',
        'Successfully defended major IP infringement cases',
        'Regular speaker at international IP law conferences',
        'Recognized by IP Law360 as "IP MVP" for three consecutive years'
      ]
    },
    {
      name: 'Alexander Thompson',
      title: 'Senior Partner',
      specialization: 'Regulatory Compliance & Government Contracting',
      description: 'Alexander brings extensive experience advising clients on regulatory compliance, government contracts, and anti-corruption matters. As a former federal prosecutor, he provides unique insights into regulatory enforcement and compliance program development. Alexander has designed compliance programs for numerous organizations and successfully represented clients in government investigations. His expertise includes FCPA compliance, government procurement regulations, and white-collar defense. He is frequently called upon to conduct internal investigations and develop risk mitigation strategies for multinational corporations.',
      education: [
        'J.D., Yale Law School',
        'M.P.A., Harvard Kennedy School',
        'B.A., Political Science, Princeton University'
      ],
      achievements: [
        'Former Assistant U.S. Attorney, Criminal Division',
        'Designed compliance programs for numerous organizations',
        'Expert witness in regulatory matters before Congress',
        'Published "Modern Compliance Strategies" handbook adopted by major companies'
      ]
    },
    {
      name: 'Diana Rodriguez',
      title: 'Partner',
      specialization: 'Immigration & Employment-Based Visas',
      description: 'Diana leads our immigration practice with comprehensive experience helping businesses and individuals navigate complex immigration processes. She has secured visas and green cards for numerous clients from around the world. Diana specializes in employment-based immigration, including H-1B, L-1, O-1, and EB-5 investor visas. She also handles family-based immigration, naturalization, and deportation defense. Fluent in Spanish and Mandarin, Diana provides culturally sensitive counsel to diverse clientele and works closely with corporate HR departments to develop immigration strategies for global workforce mobility.',
      education: [
        'J.D., Columbia Law School',
        'LL.M., Immigration Law, Georgetown University Law Center',
        'B.A., International Relations, University of California'
      ],
      achievements: [
        'Excellent track record with visa applications',
        'Successfully handled numerous immigration cases',
        'Fluent in Spanish and Mandarin Chinese',
        'Recognized by American Immigration Lawyers Association for excellence',
        'Frequent contributor to immigration law journals and publications'
      ]
    },
    {
      name: 'Marcus Williams',
      title: 'Paralegal',
      specialization: 'Business Litigation & Dispute Resolution',
      description: 'Marcus represents clients in complex commercial litigation and alternative dispute resolution proceedings. With a track record of favorable outcomes in high-stakes disputes, he handles contract disputes, partnership disagreements, fraud claims, and business torts. Marcus is skilled in both courtroom advocacy and strategic settlement negotiations. His pragmatic approach focuses on cost-effective resolution while vigorously protecting client interests. He has successfully resolved significant disputes through trial, arbitration, and mediation.',
      education: [
        'J.D., University of Chicago Law School',
        'B.A., Philosophy, Duke University'
      ],
      achievements: [
        'Successfully tried numerous cases to verdict',
        'Recovered significant compensation for clients in commercial disputes',
        'Certified mediator and arbitrator',
        'Named "Litigator of the Year" by Business Law Journal',
        'Frequent lecturer on litigation strategy and trial advocacy'
      ]
    },
    {
      name: 'Sophia Anderson',
      title: 'Paralegal',
      specialization: 'Non-Profit Law & Tax-Exempt Organizations',
      description: 'Sophia specializes in representing non-profit organizations, helping them achieve and maintain tax-exempt status while ensuring ongoing compliance with federal regulations. She advises on governance matters, charitable giving programs, unrelated business income, and employment issues specific to non-profits. Sophia has successfully guided numerous organizations through IRS examinations and audits. Her practice also includes helping social enterprises structure hybrid models that combine charitable mission with revenue generation.',
      education: [
        'J.D., Georgetown University Law Center',
        'LL.M., Taxation, New York University School of Law',
        'B.A., Economics, Boston College'
      ],
      achievements: [
        'Secured 501(c)(3) status for numerous organizations',
        'Board member of multiple non-profit organizations',
        'Published comprehensive guide on non-profit compliance',
        'Successfully defended organizations in IRS examinations with no adverse findings'
      ]
    },
    {
      name: 'Jonathan Lee',
      title: 'Paralegal',
      specialization: 'Securities Law & Corporate Finance',
      description: 'Jonathan advises companies on securities law compliance, capital raising transactions, and corporate finance matters. He has extensive experience with public offerings, private placements, SPAC transactions, and venture capital financings. Jonathan counsels public companies on SEC reporting obligations, proxy statements, and corporate governance requirements. His practice includes representing issuers, underwriters, and investors in a wide range of securities transactions. He stays current with evolving securities regulations and market practices to provide practical, up-to-date advice.',
      education: [
        'J.D., University of Pennsylvania Law School',
        'B.S., Finance, Wharton School of Business',
        'CFA Charterholder'
      ],
      achievements: [
        'Completed numerous securities offerings',
        'Advised on multiple IPOs and SPAC transactions',
        'Regular contributor to securities law publications',
        'Rising Star designation by Securities Law Review'
      ]
    },
    {
      name: 'Catherine Morrison',
      title: 'Paralegal',
      specialization: 'Employment Law & Labor Relations',
      description: 'Catherine represents employers in all aspects of employment law, including discrimination claims, wrongful termination, wage and hour disputes, and employment contract negotiations. She counsels clients on compliance with federal employment laws, develops employee handbooks and policies, and provides training on harassment prevention and discrimination issues. Catherine also handles labor relations matters, including union negotiations and unfair labor practice charges. Her proactive approach helps clients avoid litigation while effectively defending them when disputes arise.',
      education: [
        'J.D., Northwestern University Pritzker School of Law',
        'M.A., Labor Relations, Cornell University',
        'B.A., Psychology, University of Michigan'
      ],
      achievements: [
        'Successfully defended numerous employment-related claims',
        'Developed employment policies adopted by major corporations',
        'Certified SHRM Senior Professional (SHRM-SCP)',
        'Named "Rising Star in Employment Law" by HR Legal Review',
        'Frequent speaker at HR and employment law conferences'
      ]
    }
  ];

  return (
    <div className="min-h-screen" data-testid="attorneys-page">
      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1687289133469-b2a07a13b78b?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Legal scales of justice"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-900/70"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Our Attorneys</h1>
            <p className="text-xl md:text-2xl text-slate-300">
              Meet the experienced legal professionals dedicated to your success.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Team Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-lg text-slate-600 leading-relaxed">
              Our attorneys bring together diverse backgrounds, specialized expertise, and a shared commitment to 
              excellence. With extensive combined experience, we provide sophisticated legal services across multiple 
              practice areas. Each member of our team is dedicated to understanding your unique needs and delivering 
              results that exceed expectations.
            </p>
          </div>
        </div>
      </section>

      {/* Attorneys List */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Attorney cards stack vertically on all screen sizes */}
          <div className="space-y-16">
            {attorneys.map((attorney, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white border border-slate-100 shadow-sm p-8 md:p-12"
              >
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-slate-900 flex items-center justify-center">
                    <Award className="h-8 w-8 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">{attorney.name}</h2>
                    <p className="text-xl text-amber-700 font-medium mb-1">{attorney.title}</p>
                    <p className="text-lg text-slate-600">{attorney.specialization}</p>
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-6 space-y-6">
                  <p className="text-slate-700 leading-relaxed text-lg">
                    {attorney.description}
                  </p>

                  {/* Education */}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <GraduationCap className="h-5 w-5 text-amber-700" />
                      Education
                    </h3>
                    <ul className="space-y-2">
                      {attorney.education.map((edu, eduIdx) => (
                        <li key={eduIdx} className="flex items-start gap-2 text-slate-600">
                          <span className="text-amber-700 mt-1">•</span>
                          <span>{edu}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Professional Achievements */}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-amber-700" />
                      Professional Achievements
                    </h3>
                    <ul className="space-y-2">
                      {attorney.achievements.map((achievement, achIdx) => (
                        <li key={achIdx} className="flex items-start gap-2 text-slate-600">
                          <span className="text-amber-700 mt-1">•</span>
                          <span>{achievement}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Work With Our Team</h2>
          <p className="text-xl text-slate-300 mb-8">
            Schedule a consultation with one of our experienced attorneys today.
          </p>
          <button
            onClick={openLeadForm}
            className="bg-amber-700 text-white hover:bg-amber-800 px-8 py-4 font-serif text-lg transition-colors shadow-lg hover:shadow-xl"
            data-testid="attorneys-cta-button"
          >
            Schedule Consultation
          </button>
        </div>
      </section>
    </div>
  );
};

export default OurAttorneys;
